import React, { useState, useMemo, useEffect, useRef } from 'react';
import { DollarSign, Landmark, Package, Wallet, CheckCircle2, BarChart3, Info, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';

import { Card } from '../../components/ui/Card';
import { StatisticsChart } from './StatisticsChart';
import { CashFlowChart } from './CashFlowChart';
import { formatCurrency, formatNumber, getLocalDateString, calculateOrderProfit } from '../../utils/helpers';

// A new component for the tooltip icon that works on click
const InfoTooltip = ({ text }) => {
    const [isOpen, setIsOpen] = useState(false);
    const tooltipRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative flex items-center" ref={tooltipRef}>
            <button onClick={() => setIsOpen(o => !o)}>
                <Info size={14} className="text-gray-400 cursor-pointer" />
            </button>
            {isOpen && (
                <div className="absolute bottom-full mb-2 w-64 p-3 text-sm text-white bg-gray-900 rounded-md shadow-lg z-10">
                    {text}
                </div>
            )}
        </div>
    );
};


export const Dashboard = ({ orders, transactions, accounts }) => {
    const [timeRange, setTimeRange] = useState('this_month');
    const [chartGroupBy, setChartGroupBy] = useState('Ngày');
    
    const [customStartDate, setCustomStartDate] = useState(() => {
        const date = new Date();
        date.setDate(1);
        return date.toISOString().split('T')[0];
    });
    const [customEndDate, setCustomEndDate] = useState(getLocalDateString());


    const filteredData = useMemo(() => {
        const now = new Date();
        let startDate, endDate;

        if (timeRange === 'custom') {
            if (!customStartDate || !customEndDate) {
                return { filteredOrders: [], filteredTransactions: [], startDate: '', endDate: '' };
            }
            startDate = new Date(customStartDate);
            endDate = new Date(customEndDate);
        } else {
            endDate = new Date();
            switch (timeRange) {
                case 'today':
                    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    break;
                case 'this_week':
                    const dayOfWeek = now.getDay() === 0 ? 6 : now.getDay() - 1; 
                    startDate = new Date(now);
                    startDate.setDate(now.getDate() - dayOfWeek);
                    break;
                case 'this_month':
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    break;
                case 'last_month':
                    startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    endDate = new Date(now.getFullYear(), now.getMonth(), 0);
                    break;
                case 'last_3_months':
                    startDate = new Date(new Date().setMonth(now.getMonth() - 3));
                    break;
                 case 'last_6_months':
                    startDate = new Date(new Date().setMonth(now.getMonth() - 6));
                    break;
                case 'this_year':
                     startDate = new Date(now.getFullYear(), 0, 1);
                     break;
                default: // all_time
                    startDate = transactions && transactions.length > 0 
                        ? new Date(Math.min(...(transactions.map(t => new Date(t.date)))))
                        : new Date();
            }
        }
        
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        const filterFunc = (item) => {
            if (!item || !item.date) return false;
            const itemDate = new Date(item.date);
            return itemDate >= startDate && itemDate <= endDate;
        };

        const filteredOrders = Array.isArray(orders) ? orders.filter(filterFunc) : [];
        const filteredTransactions = Array.isArray(transactions) ? transactions.filter(filterFunc) : [];

        return { 
            filteredOrders, 
            filteredTransactions, 
            startDate: startDate.toISOString().split('T')[0], 
            endDate: endDate.toISOString().split('T')[0] 
        };

    }, [orders, transactions, timeRange, customStartDate, customEndDate]);


     const kpiData = useMemo(() => {
        const { filteredOrders, filteredTransactions } = filteredData;
        
        const revenueStatuses = ["Mới", "Đã đặt hàng", "Đã giao hàng", "Đã hoàn thành", "Đã đối soát"];
        const costOfGoodsExpenseCategories = ['Ứng tiền PO', 'Mua hàng SCGR', 'Mua hàng Mộc Hương', 'Mua hàng STB', 'Mua hàng khác', 'Mua hàng NXB'];
        const costOfGoodsIncomeCategory = 'Hoàn tiền PO';

        let kpis = { 
            totalRevenue: 0, totalCashIn: 0, totalCostOfGoods: 0, totalOpEx: 0, grossProfit: 0, netProfit: 0,
            totalOrders: 0, avgSalePerDay: 0, avgOrderValue: 0, avgProfitPerOrder: 0,
            salesBySource: {},
            profitBySource: {},
            profitPercentageBySource: {},
            warehouseStats: { 'Kho DL': { packedOrders: 0, capitalContribution: 0 }, 'Kho HCM': { packedOrders: 0, capitalContribution: 0 }, 'Kho khác': { packedOrders: 0, capitalContribution: 0 } },
            shippingStats: { totalCustomerShippingFee: 0, totalRealShippingFee: 0, transferFee: 0, shippingDifference: 0 },
            costManagement: {
                income: { reconciliationOreka: 0, reconciliationLazada: 0, reconciliationOther: 0, otherIncome: 0, capital: 0, total: 0 },
                expense: { bookPurchasePO: 0, purchaseSCGR: 0, purchaseMocHuong: 0, purchaseSTB: 0, purchaseOther: 0, tax: 0, otherExpense: 0, total: 0 }
            }
        };
        
        const sources = ['Trực tiếp', 'Website', 'Oreka', 'Lazada'];
        sources.forEach(s => {
            kpis.salesBySource[s] = 0;
            kpis.profitBySource[s] = 0;
            kpis.profitPercentageBySource[s] = 0;
        });

        const costOfGoodsExpenses = filteredTransactions
            .filter(t => t.type === 'Chi' && costOfGoodsExpenseCategories.includes(t.category))
            .reduce((sum, t) => sum + t.amount, 0);
        const costOfGoodsIncome = filteredTransactions
            .filter(t => t.type === 'Thu' && t.category === costOfGoodsIncomeCategory)
            .reduce((sum, t) => sum + t.amount, 0);
        kpis.totalCostOfGoods = costOfGoodsExpenses - costOfGoodsIncome;

        const revenueOrders = filteredOrders.filter(order => revenueStatuses.includes(order.status));

        revenueOrders.forEach(order => {
            if (!Array.isArray(order.items)) return;
            const itemsValue = order.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
            const orderRevenue = itemsValue + (order.shippingFee || 0) - (order.promotion || 0);
            const orderOpEx = (order.realShippingFee || 0) + (order.platformFee || 0);
            const { profit: orderProfit } = calculateOrderProfit(order);

            kpis.totalRevenue += orderRevenue;
            kpis.totalOpEx += orderOpEx;
            if (orderProfit !== null) { kpis.netProfit += orderProfit; }
            if (kpis.salesBySource.hasOwnProperty(order.source)) {
                kpis.salesBySource[order.source] += orderRevenue;
                if(orderProfit !== null) kpis.profitBySource[order.source] += orderProfit;
            }
            if (kpis.warehouseStats[order.packingWarehouse]) { kpis.warehouseStats[order.packingWarehouse].packedOrders += 1; }
            if (order.label === 'Góp vốn hàng (Daklak)') { kpis.warehouseStats['Kho DL'].capitalContribution += itemsValue; } 
            else if (order.label === 'Góp vốn hàng (HCM)') { kpis.warehouseStats['Kho HCM'].capitalContribution += itemsValue; }
            
            kpis.shippingStats.totalCustomerShippingFee += order.shippingFee || 0;
            kpis.shippingStats.totalRealShippingFee += order.realShippingFee || 0;
        });
        
        sources.forEach(s => {
            const revenue = kpis.salesBySource[s];
            const profit = kpis.profitBySource[s];
            kpis.profitPercentageBySource[s] = revenue > 0 ? (profit / revenue) * 100 : 0;
        });

        kpis.totalCashIn = filteredTransactions
            .filter(t => t.type === 'Thu' && t.category !== 'Hoàn tiền PO')
            .reduce((sum, t) => sum + t.amount, 0);

        const transactionOpEx = filteredTransactions
            .filter(t => t.type === 'Chi' && ['Trả lương', 'Luân chuyển hàng', 'Đóng thuế', 'Chi khác'].includes(t.category))
            .reduce((sum, t) => sum + t.amount, 0);
        
        kpis.totalOpEx += transactionOpEx;
        kpis.grossProfit = kpis.totalRevenue - kpis.totalCostOfGoods - kpis.totalOpEx;
        kpis.totalOrders = revenueOrders.length;
        kpis.avgOrderValue = kpis.totalOrders > 0 ? kpis.totalRevenue / kpis.totalOrders : 0;
        kpis.avgProfitPerOrder = kpis.totalOrders > 0 ? kpis.netProfit / kpis.totalOrders : 0;
        
        const dayDifference = (d2, d1) => Math.ceil(Math.abs(d1 - d2) / (1000 * 60 * 60 * 24));
        const firstDate = revenueOrders.length > 0 ? new Date(Math.min(...revenueOrders.map(o => new Date(o.date)))) : new Date();
        const numberOfDays = dayDifference(new Date(), firstDate) || 1;
        kpis.avgSalePerDay = numberOfDays > 0 ? kpis.totalRevenue / numberOfDays : 0;
        
        kpis.shippingStats.transferFee = filteredTransactions
            .filter(t => t.type === 'Chi' && t.category === 'Luân chuyển hàng')
            .reduce((sum, t) => sum + t.amount, 0);
        kpis.shippingStats.shippingDifference = kpis.shippingStats.totalCustomerShippingFee - kpis.shippingStats.totalRealShippingFee;

        // *** NEW: Cost Management Calculation ***
        const { income, expense } = kpis.costManagement;
        const ungTienPO = filteredTransactions.filter(t => t.type === 'Chi' && t.category === 'Ứng tiền PO').reduce((s, t) => s + t.amount, 0);
        const muaHangNXB = filteredTransactions.filter(t => t.type === 'Chi' && t.category === 'Mua hàng NXB').reduce((s, t) => s + t.amount, 0);
        const hoanTienPO = filteredTransactions.filter(t => t.type === 'Thu' && t.category === 'Hoàn tiền PO').reduce((s, t) => s + t.amount, 0);
        expense.bookPurchasePO = ungTienPO + muaHangNXB - hoanTienPO;

        filteredTransactions.forEach(t => {
            if (t.type === 'Thu') {
                switch(t.category) {
                    case 'Đối soát Oreka': income.reconciliationOreka += t.amount; break;
                    case 'Đối soát Lazada': income.reconciliationLazada += t.amount; break;
                    case 'Đối soát Nhanh.vn':
                    case 'Đối soát Khác': income.reconciliationOther += t.amount; break;
                    case 'Lãi ngân hàng':
                    case 'Thu khác': income.otherIncome += t.amount; break;
                    case 'Góp vốn DL':
                    case 'Góp vốn HCM': income.capital += t.amount; break;
                    default: break;
                }
            } else { // type === 'Chi'
                switch(t.category) {
                    case 'Mua hàng SCGR': expense.purchaseSCGR += t.amount; break;
                    case 'Mua hàng Mộc Hương': expense.purchaseMocHuong += t.amount; break;
                    case 'Mua hàng STB': expense.purchaseSTB += t.amount; break;
                    case 'Mua hàng khác': expense.purchaseOther += t.amount; break;
                    case 'Đóng thuế': expense.tax += t.amount; break;
                    case 'Chi khác': expense.otherExpense += t.amount; break;
                    default: break;
                }
            }
        });
        income.total = Object.values(income).reduce((s, v) => s + v, 0);
        expense.total = Object.values(expense).reduce((s, v) => s + v, 0);


        return kpis;
    }, [filteredData.filteredOrders, filteredData.filteredTransactions]);
    
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-800">Tổng quan kinh doanh</h2>
                <div className="flex items-center gap-2">
                    <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="p-2 border rounded-md bg-white shadow-sm">
                        <option value="today">Hôm nay</option>
                        <option value="this_week">Tuần này</option>
                        <option value="this_month">Tháng này</option>
                        <option value="last_month">Tháng trước</option>
                        <option value="last_3_months">3 tháng qua</option>
                        <option value="last_6_months">6 tháng qua</option>
                        <option value="this_year">Năm nay</option>
                        <option value="all_time">Toàn thời gian</option>
                        <option value="custom">Tùy chọn</option>
                    </select>
                     {timeRange === 'custom' && (
                        <div className="flex items-center gap-2 bg-white p-1 rounded-md shadow-sm border">
                            <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className="p-1 border-none rounded-md" />
                            <span>-</span>
                            <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className="p-1 border-none rounded-md" />
                        </div>
                     )}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
                <Card title="Tổng Doanh Thu" value={formatCurrency(kpiData.totalRevenue)} icon={<DollarSign />} >
                    <InfoTooltip text="Tổng tiền khách trả từ các đơn hàng có trạng thái: Mới, Đã đặt hàng, Đã giao hàng, Đã hoàn thành, Đã đối soát." />
                </Card>
                <Card title="Tổng Tiền Vào" value={formatCurrency(kpiData.totalCashIn)} icon={<Landmark />} >
                     <InfoTooltip text="Tổng tiền từ các phiếu THU, không bao gồm các phiếu có hạng mục 'Hoàn tiền PO'." />
                </Card>
                <Card title="Tổng Tiền Hàng" value={formatCurrency(kpiData.totalCostOfGoods)} icon={<Package />} >
                     <InfoTooltip text="Tổng các phiếu CHI mua hàng (Ứng tiền PO, Mua hàng...) trừ đi phiếu THU Hoàn tiền PO." />
                </Card>
                <Card title="Tổng Chi Phí" value={formatCurrency(kpiData.totalOpEx)} icon={<Wallet />} >
                    <InfoTooltip text="Bao gồm: Phí ship thực trả, Phí sàn TMĐT, và các phiếu CHI có hạng mục: Trả lương, Luân chuyển hàng, Đóng thuế, Chi khác." />
                </Card>
                <Card title="Lợi Nhuận Gộp" value={formatCurrency(kpiData.grossProfit)} icon={<CheckCircle2 />} >
                    <InfoTooltip text="= (Tổng Doanh Thu) - (Tổng Tiền Hàng) - (Tổng Chi Phí)." />
                </Card>
                <Card title="Lợi Nhuận Ròng" value={formatCurrency(kpiData.netProfit)} icon={<BarChart3 />} >
                     <InfoTooltip text="Tổng lợi nhuận thực tế của từng đơn hàng (đã trừ hết các loại chi phí của đơn hàng đó)." />
                </Card>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md space-y-8">
                <div>
                     <h3 className="text-lg font-semibold text-gray-700 mb-4">Thông số tham khảo</h3>
                     <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <h4 className="text-sm font-medium text-gray-500">Tổng số đơn hàng</h4>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{formatNumber(kpiData.totalOrders)}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <h4 className="text-sm font-medium text-gray-500">Doanh số trung bình/ngày</h4>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(kpiData.avgSalePerDay)}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <h4 className="text-sm font-medium text-gray-500">Giá trị trung bình/đơn</h4>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(kpiData.avgOrderValue)}</p>
                        </div>
                         <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <h4 className="text-sm font-medium text-gray-500">Lợi nhuận trung bình/đơn</h4>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(kpiData.avgProfitPerOrder)}</p>
                        </div>
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Doanh số & Lợi nhuận theo sàn</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {Object.keys(kpiData.salesBySource).map(source => (
                            <div key={source} className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-base font-bold text-gray-800">{source}</h4>
                                <div className="mt-2 text-sm space-y-1">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Doanh số:</span>
                                        <span className="font-semibold">{formatCurrency(kpiData.salesBySource[source])}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Lợi nhuận:</span>
                                        <span className={`font-semibold ${kpiData.profitBySource[source] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatCurrency(kpiData.profitBySource[source])}
                                            <span className="text-xs ml-1 text-gray-500">({kpiData.profitPercentageBySource[source].toFixed(1)}%)</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* *** NEW: COST MANAGEMENT SECTION *** */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Quản lý chi phí</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Income Column */}
                        <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                                <ArrowUpCircle className="text-green-600" />
                                <h4 className="text-base font-bold text-green-800">Thu</h4>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-gray-600">Đối soát Oreka:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.income.reconciliationOreka)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Đối soát Lazada:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.income.reconciliationLazada)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Đối soát Khác:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.income.reconciliationOther)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Thu khác:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.income.otherIncome)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Góp vốn:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.income.capital)}</span></div>
                                <div className="flex justify-between border-t mt-2 pt-2 font-bold text-green-700"><span >Tổng Thu:</span> <span>{formatCurrency(kpiData.costManagement.income.total)}</span></div>
                            </div>
                        </div>
                        {/* Expense Column */}
                        <div className="bg-red-50 p-4 rounded-lg">
                             <div className="flex items-center gap-2 mb-3">
                                <ArrowDownCircle className="text-red-600" />
                                <h4 className="text-base font-bold text-red-800">Chi</h4>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-gray-600">Mua sách PO:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.expense.bookPurchasePO)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Mua hàng SCGR:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.expense.purchaseSCGR)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Mua hàng Mộc Hương:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.expense.purchaseMocHuong)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Mua hàng STB:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.expense.purchaseSTB)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Mua hàng khác:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.expense.purchaseOther)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Đóng thuế:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.expense.tax)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-600">Chi khác:</span> <span className="font-semibold">{formatCurrency(kpiData.costManagement.expense.otherExpense)}</span></div>
                                <div className="flex justify-between border-t mt-2 pt-2 font-bold text-red-700"><span >Tổng Chi:</span> <span>{formatCurrency(kpiData.costManagement.expense.total)}</span></div>
                            </div>
                        </div>
                    </div>
                </div>


                <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Hoạt động đa kho</h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {Object.entries(kpiData.warehouseStats).map(([name, stats]) => (
                            <div key={name} className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-base font-bold text-gray-800">{name}</h4>
                                <div className="mt-2 text-sm space-y-1">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Số đơn đã đóng:</span>
                                        <span className="font-semibold">{formatNumber(stats.packedOrders)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Tổng vốn góp:</span>
                                        <span className="font-semibold">{formatCurrency(stats.capitalContribution)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Chi phí vận chuyển</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <h4 className="text-sm font-medium text-gray-500">Tổng ship báo khách</h4>
                            <p className="text-2xl font-bold text-green-600 mt-2">{formatCurrency(kpiData.shippingStats.totalCustomerShippingFee)}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <h4 className="text-sm font-medium text-gray-500">Tổng ship thực trả</h4>
                            <p className="text-2xl font-bold text-red-600 mt-2">{formatCurrency(kpiData.shippingStats.totalRealShippingFee)}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <h4 className="text-sm font-medium text-gray-500">Chênh lệch phí ship</h4>
                            <p className={`text-2xl font-bold mt-2 ${kpiData.shippingStats.shippingDifference >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(kpiData.shippingStats.shippingDifference)}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <h4 className="text-sm font-medium text-gray-500">Phí luân chuyển hàng</h4>
                            <p className="text-2xl font-bold text-orange-600 mt-2">{formatCurrency(kpiData.shippingStats.transferFee)}</p>
                        </div>
                    </div>
                </div>

            </div>

            <div className="mt-6">
                <StatisticsChart data={filteredData.filteredOrders} groupBy={chartGroupBy} setGroupBy={setChartGroupBy} />
            </div>

            <div className="mt-6">
                <CashFlowChart 
                    transactions={transactions} 
                    accounts={accounts} 
                    startDate={filteredData.startDate} 
                    endDate={filteredData.endDate}
                />
            </div>
        </div>
    );
};
